/**
 * TikTok Events API Adapter
 *
 * Adapts standardized event payloads into TikTok Events API format.
 * Handles both browser pixel and server-to-server (S2S) data structures.
 *
 * @see https://ads.tiktok.com/marketing_api/docs?id=1701890979375106
 */

(function (pmw) {
	"use strict";

	/**
	 * Maximum number of products attached to a category ViewContent event.
	 *
	 * Keeps the Events API request small on shops that render very long product
	 * listings on a single page.
	 */
	const MAX_LISTING_CONTENTS = 50;

	/**
	 * TikTok Event Adapter
	 */
	class TikTokAdapter {
		/**
		 * Adapt event data to TikTok format
		 *
		 * @param {object} eventData - Standardized event data
		 * @param {string} eventName - Event name (e.g., "add_to_cart")
		 * @param {object} context - Additional context (user, page, etc.)
		 * @returns {object|null} TikTok-formatted event data or null if event not supported
		 */
		adapt(eventData, eventName, context) {
			// Only process if TikTok pixel is loaded
			if (!pmwDataLayer?.pixels?.tiktok?.loaded) {
				return null;
			}

			// Map event names to adaptation methods
			const eventMap = {
				page_view       : () => this.adaptPageView(eventData),
				add_to_cart     : () => this.adaptAddToCart(eventData),
				view_item       : () => this.adaptViewContent(eventData),
				begin_checkout  : () => this.adaptInitiateCheckout(eventData),
				add_payment_info: () => this.adaptAddPaymentInfo(eventData),
				purchase        : () => this.adaptPurchase(eventData),
				search          : () => this.adaptSearch(eventData),
				add_to_wishlist : () => this.adaptAddToWishlist(eventData),
				view_category   : () => this.adaptViewCategory(eventData),
				place_order     : () => this.adaptPlaceAnOrder(eventData),
				login           : () => this.adaptLogin(eventData),
			};
			if (eventMap[eventName]) {
				// Null/undefined stripping happens centrally in the event processor
				// (pmw.stripNullish), which TikTok's Events API requires: it rejects
				// the entire request when a property is explicitly null.
				return eventMap[eventName]();
			}

			return null;
		}

		/**
		 * Get base TikTok event data structure
		 *
		 * @returns {object} Base event data with event_id, user, and page
		 */
		getBaseData() {

			const data = {
				event_id  : pmw.getRandomEventId(),
				event_time: Math.floor(Date.now() / 1000),
			};

			// The user and page data the Events API needs come from
			// functions_premium.js, which the free build does not ship. The
			// browser pixel only reads event, properties and event_id.
			if (typeof pmw.tiktokGetEventUserData === "function") {
				data.user = pmw.tiktokGetEventUserData();
			}

			if (typeof pmw.tiktokGetEventPageData === "function") {
				data.page = pmw.tiktokGetEventPageData();
			}

			return data;
		}

		/**
		 * Adapt AddToCart event
		 *
		 * @param {object} eventData - Event data with product information
		 * @returns {object} TikTok AddToCart event
		 */
		adaptAddToCart(eventData) {
			const data = this.getBaseData();
			data.event = "AddToCart";

			if (eventData.product) {
				const product   = eventData.product;
				data.properties = {
					value       : product.price * product.quantity,
					currency    : pmw.getProductCurrency(product),
					content_type: "product",
					contents    : [{
						content_id  : pmw.getProductIdForSpecificPixel(product, "tiktok"),
						content_name: product.name,
						quantity    : product.quantity,
						price       : product.price,
					}],
				};
			}

			return data;
		}

		/**
		 * Adapt ViewContent event (single product view)
		 *
		 * @param {object} eventData - Event data with product information
		 * @returns {object} TikTok ViewContent event
		 */
		adaptViewContent(eventData) {
			const data = this.getBaseData();
			data.event = "ViewContent";

			if (eventData.product) {
				const product   = eventData.product;
				data.properties = {
					value       : product.price * product.quantity,
					currency    : pmw.getProductCurrency(product),
					content_type: "product",
					contents    : [{
						content_id  : pmw.getProductIdForSpecificPixel(product, "tiktok"),
						content_name: product.name,
						quantity    : product.quantity,
						price       : product.price,
					}],
				};
			}

			return data;
		}

		/**
		 * Adapt InitiateCheckout event
		 *
		 * @param {object} eventData - Event data with cart items
		 * @returns {object} TikTok InitiateCheckout event
		 */
		adaptInitiateCheckout(eventData) {
			const data = this.getBaseData();
			data.event = "InitiateCheckout";

			// Only attach commerce data when the tracked cart has contents. An
			// empty tracked cart here usually means the cart was populated outside
			// of client-side tracking, so value 0 and empty contents would report
			// false zeros. The event itself still fires, because the visitor did
			// start the checkout.
			if (pmwDataLayer?.cart && !pmw.isEmptyObject(pmwDataLayer.cart)) {
				data.properties = pmw.tiktokGetPropertiesFromCart();
			}

			return data;
		}

		/**
		 * Adapt AddPaymentInfo event
		 *
		 * @param {object} eventData - Event data
		 * @returns {object} TikTok AddPaymentInfo event
		 */
		adaptAddPaymentInfo(eventData) {
			const data = this.getBaseData();
			data.event = "AddPaymentInfo";

			// Only attach commerce data when the tracked cart has contents,
			// mirroring adaptBeginCheckout.
			if (pmwDataLayer?.cart && !pmw.isEmptyObject(pmwDataLayer.cart)) {
				data.properties = pmw.tiktokGetPropertiesFromCart();
			}

			return data;
		}

		/**
		 * Adapt Purchase event
		 *
		 * Reads order data from pmwDataLayer.order directly, matching the pattern
		 * used by the other purchase adapters (Facebook, Google Ads, etc.).
		 *
		 * @since 1.58.12
		 *
		 * @param {object} eventData - Event data (order is read from pmwDataLayer)
		 * @returns {object|null} TikTok Purchase event or null when order data is missing
		 */
		adaptPurchase(eventData) {
			const order = pmwDataLayer?.order;

			if (!order) {
				pmw.console.warn("TikTok: order data not available for Purchase event");
				return null;
			}

			const data = this.getBaseData();
			// Use order ID for purchase event to match server-side event ID
			data.event_id = "pmw_" + order.id.toString();
			data.event    = "Purchase";

			const items = Array.isArray(order.items) ? order.items : Object.values(order.items || {});

			data.properties = {
				value       : order.value?.marketing ?? 0,
				currency    : order.currency || pmwDataLayer?.shop?.currency,
				content_type: "product",
				// A line whose product data is gone (a deleted product on an older
				// order) resolves to no content id. Drop the line, keep the value:
				// it comes from the order, so the conversion stays correct.
				contents    : items
					.map(item => ({
						content_id  : pmw.getProductIdForSpecificPixel(item, "tiktok"),
						content_name: item.name,
						quantity    : item.quantity,
						price       : item.price,
					}))
					.filter(content => null !== content.content_id),
			};

			return data;
		}

		/**
		 * Adapt Search event
		 *
		 * @param {object} eventData - Event data with search query
		 * @returns {object} TikTok Search event
		 */
		adaptSearch(eventData) {
			const data = this.getBaseData();
			data.event = "Search";

			data.properties = {
				query: pmw.getSearchTermFromUrl(),
			};

			return data;
		}

		/**
		 * Adapt AddToWishlist event
		 *
		 * @param {object} eventData - Event data with product information
		 * @returns {object} TikTok AddToWishlist event
		 */
		adaptAddToWishlist(eventData) {
			const data = this.getBaseData();
			data.event = "AddToWishlist";

			if (eventData.product) {
				const product   = eventData.product;
				data.properties = {
					value       : product.price * product.quantity,
					currency    : pmw.getProductCurrency(product),
					content_type: "product",
					contents    : [{
						content_id  : pmw.getProductIdForSpecificPixel(product, "tiktok"),
						content_name: product.name,
						quantity    : product.quantity,
						price       : product.price,
					}],
				};
			}

			return data;
		}

		/**
		 * Adapt ViewCategory event (category page view)
		 *
		 * TikTok has no dedicated category event, so a category view is reported as
		 * ViewContent carrying the products of the listing. TikTok requires a
		 * content_id on commerce events and flags events without one as critical in
		 * the Events Manager diagnostics ("Content ID is missing in your events"),
		 * which is why the products the visitor actually sees are attached instead
		 * of a bare content_type.
		 *
		 * No value is sent: summing up every listed product would report a purchase
		 * intent that never happened and would distort TikTok's optimization.
		 *
		 * @since 1.64.1
		 *
		 * @param {object} eventData - Event data (may be minimal)
		 * @returns {object} TikTok ViewContent event
		 */
		adaptViewCategory(eventData) {
			const data = this.getBaseData();
			data.event = "ViewContent";

			const productIds = pmw.platform.getProductIdsOnPage();

			// Without a single resolvable product there is nothing to attach. Sending
			// content_type on its own would produce exactly the content_id-less event
			// TikTok rejects, so the properties are left off entirely.
			if (productIds.length === 0) {
				pmw.console.log("TikTok: no listed products found for the ViewContent category event");
				return data;
			}

			// TikTok doesn't document a limit for contents, so long listings are
			// capped to keep the request small. The products at the top of the
			// listing are the ones the visitor is most likely to have seen.
			const cappedProductIds = productIds.slice(0, MAX_LISTING_CONTENTS);

			if (cappedProductIds.length < productIds.length) {
				pmw.console.log(`TikTok: ViewContent contents capped at ${MAX_LISTING_CONTENTS} of ${productIds.length} listed products`);
			}

			data.properties = {
				currency    : pmwDataLayer?.shop?.currency,
				content_type: "product",
				contents    : cappedProductIds.map(productId => {
					const product = pmwDataLayer.products[productId];
					return {
						content_id  : pmw.getProductIdForSpecificPixel(product, "tiktok"),
						content_name: product.name,
						price       : product.price,
					};
				}),
			};

			return data;
		}

		/**
		 * Adapt PageView event
		 *
		 * Note: TikTok doesn't have a specific PageView event in their standard events.
		 * We return null as TikTok doesn't support page view tracking via Events API.
		 *
		 * @param {object} eventData - Event data
		 * @returns {null} TikTok doesn't support page view
		 */
		adaptPageView(eventData) {
			// TikTok Events API doesn't have a PageView event
			// Return null to indicate this event is not supported
			return null;
		}

		/**
		 * Adapt PlaceAnOrder event
		 *
		 * @param {object} eventData - Event data
		 * @returns {object} TikTok PlaceAnOrder event
		 */
		adaptPlaceAnOrder(eventData) {
			const data = this.getBaseData();
			data.event = "PlaceAnOrder";

			// Only attach commerce data when the tracked cart has contents,
			// mirroring adaptInitiateCheckout.
			if (pmwDataLayer?.cart && !pmw.isEmptyObject(pmwDataLayer.cart)) {
				data.properties = pmw.tiktokGetPropertiesFromCart();
			}

			return data;
		}

		/**
		 * Adapt Login event
		 *
		 * Note: TikTok doesn't have a standard Login event.
		 * Return null as this event is not supported.
		 *
		 * @param {object} eventData - Event data
		 * @returns {null} TikTok doesn't support login event
		 */
		adaptLogin(eventData) {
			// TikTok Events API doesn't have a Login event
			return null;
		}
	}

	// Register the adapter with S2S capabilities
	const adapter = new TikTokAdapter();
	pmw.registerAdapter("tiktok", adapter, {
		supportsS2S               : true,
		supportsEventDeduplication: true,
		supportsPageViewS2S       : false,
		isActive                  : () => pmwDataLayer?.pixels?.tiktok?.loaded === true && pmw.consent.categories.get().marketing === true,
		isConfigured              : () => !!pmwDataLayer?.pixels?.tiktok?.pixel_id,
	});

})(window.pmw = window.pmw || {});
